import { useParams, useSearch } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductionDeclarationMaterialCommentsSection } from '@/features/production-declaration/components/statement/production-declaration-material-comments-section';
import { ProductionDeclarationMaterialCommodityOriginCard } from '@/features/production-declaration/components/statement/production-declaration-material-commodity-origin-card';
import { ProductionDeclarationMaterialSupplyChainDialog } from '@/features/production-declaration/components/statement/production-declaration-material-supply-chain-dialog';
import { ProductionDeclarationMaterialSupplyChainList } from '@/features/production-declaration/components/statement/production-declaration-material-supply-chain-list';
import { useCanEditProductionDeclaration } from '@/features/production-declaration/hooks/use-can-edit-production-declaration';
import { useProductionDeclarationCorrectionRequestComment } from '@/features/production-declaration/hooks/use-production-declaration-correction-request-comment';
import { useReorderSupplyChainInformations } from '@/services/apis/mutations/production-declaration/use-reorder-supply-chain-informations';
import { useGetSupplyChainInformations } from '@/services/apis/queries/production-declaration/use-get-supply-chain-informations';
import { CorrectionRequestEntityType } from '@/types/core/purchase-order/correction-request';
import type { ProductionDeclarationStatusCode as ProductionDeclarationStatusCodeType } from '@/types/core/purchase-order/production-declaration-status-code';

type ProductionDeclarationMaterialSupplyChainCardProps = {
  canComment?: boolean;
  commodityOriginId: string;
  showSupplierCorrectionComments?: boolean;
  statusCode: ProductionDeclarationStatusCodeType;
};

export function ProductionDeclarationMaterialSupplyChainCard({
  canComment = false,
  commodityOriginId,
  showSupplierCorrectionComments = false,
  statusCode,
}: ProductionDeclarationMaterialSupplyChainCardProps) {
  const { id, materialId } = useParams({ strict: false });
  const { token } = useSearch({ strict: false });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });

  const productionDeclarationId = id ?? '';
  const currentMaterialId = materialId ?? '';
  const { comments, create, edit, remove, resolution } =
    useProductionDeclarationCorrectionRequestComment({
      productionDeclarationId,
      materialId: currentMaterialId,
      commodityOriginId,
      entityType: CorrectionRequestEntityType.SupplyChainInformations,
      token,
    });
  const canManageSupplyChain = useCanEditProductionDeclaration(statusCode);

  const { data: supplyChainItems = [], isLoading } =
    useGetSupplyChainInformations(
      productionDeclarationId,
      currentMaterialId,
      commodityOriginId,
      token,
    );

  const { mutate: reorder } = useReorderSupplyChainInformations();

  const handleReorder = useCallback(
    (orderedIds: string[]) => {
      reorder({
        productionDeclarationId,
        materialId: currentMaterialId,
        commodityOriginId,
        orderedIds,
        token,
      });
    },
    [
      commodityOriginId,
      currentMaterialId,
      productionDeclarationId,
      reorder,
      token,
    ],
  );

  if (!(id && materialId)) {
    return null;
  }

  return (
    <>
      <ProductionDeclarationMaterialCommodityOriginCard
        actionDisabled={!canManageSupplyChain}
        actionLabel={t('materialCommodityOrigins.cards.supplyChain.cta')}
        description={
          supplyChainItems.length === 0
            ? t('materialCommodityOrigins.cards.supplyChain.empty')
            : t('materialCommodityOrigins.cards.supplyChain.description')
        }
        onActionClick={
          canManageSupplyChain ? () => setIsDialogOpen(true) : undefined
        }
        onCommentClick={canComment ? create.open : undefined}
        title={t('materialCommodityOrigins.cards.supplyChain.title')}
        tooltipContent={t(
          'materialCommodityOrigins.cards.supplyChain.guideline',
        )}
      >
        <div className='space-y-6'>
          <ProductionDeclarationMaterialCommentsSection
            canComment={canComment}
            comments={comments}
            create={create}
            edit={edit}
            remove={remove}
            resolution={resolution}
            showSupplierCorrectionComments={showSupplierCorrectionComments}
          />
          <ProductionDeclarationMaterialSupplyChainList
            canReorder={canManageSupplyChain}
            commodityOriginId={commodityOriginId}
            isLoading={isLoading}
            items={supplyChainItems}
            materialId={currentMaterialId}
            onReorder={handleReorder}
            productionDeclarationId={productionDeclarationId}
            token={token}
          />
        </div>
      </ProductionDeclarationMaterialCommodityOriginCard>
      <ProductionDeclarationMaterialSupplyChainDialog
        commodityOriginId={commodityOriginId}
        materialId={currentMaterialId}
        onOpenChange={setIsDialogOpen}
        open={isDialogOpen}
        productionDeclarationId={productionDeclarationId}
        token={token}
      />
    </>
  );
}
